/**
 * VIP Offers Hook using TanStack Query
 * Simplified VIP offer management with automatic cache invalidation
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { VipOffersResource } from '../../core/resources/vipOffers';
import { getGlobalApiClient } from './useApiQuery';
export function useVipOffersQuery(options) {
    const { sessionId, vipOfferIds, enabled = true, onSelectSuccess, onCancelSuccess } = options;
    const queryClient = useQueryClient();
    // Create VIP offers resource client
    const vipOffersResource = useMemo(() => {
        try {
            return new VipOffersResource(getGlobalApiClient());
        }
        catch (error) {
            throw new Error('Failed to initialize VIP offers resource: ' +
                (error instanceof Error ? error.message : 'Unknown error'));
        }
    }, []);
    // Convert offer IDs to VipOffer objects
    const vipOffers = useMemo(() => {
        return vipOfferIds.map((id) => ({
            id,
            productId: '', // These will be populated from preview data
            variantId: '', // These will be populated from preview data
        }));
    }, [vipOfferIds]);
    // Main VIP preview query
    const { data: vipPreview, isLoading: isLoadingVipPreview, error: fetchError, refetch: refetchPreview, } = useQuery({
        queryKey: ['vip-preview', sessionId, vipOfferIds],
        queryFn: () => vipOffersResource.getVipPreview(sessionId, vipOfferIds),
        enabled: enabled && !!sessionId && vipOfferIds.length > 0,
        staleTime: 30000, // 30 seconds
        refetchOnWindowFocus: false,
    });
    // Check if a specific VIP offer is selected
    const isOfferSelected = useCallback((offer) => {
        if (!vipPreview?.selectedOffers)
            return false;
        return vipPreview.selectedOffers.some((selected) => selected.productId === offer.productId &&
            selected.variantId === offer.variantId &&
            selected.isSelected);
    }, [vipPreview?.selectedOffers]);
    // Calculate hasVip based on selected offers
    const hasVip = useMemo(() => {
        return vipPreview?.selectedOffers?.some((offer) => offer.isSelected) ?? false;
    }, [vipPreview?.selectedOffers]);
    // Toggle order bump mutation
    const toggleMutation = useMutation({
        mutationFn: ({ offerId, selected }) => {
            if (!sessionId) {
                throw new Error('No session ID available');
            }
            return vipOffersResource.toggleOrderBump(sessionId, offerId, selected);
        },
        onSuccess: async () => {
            // Invalidate both VIP preview and checkout queries
            if (sessionId) {
                await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ['vip-preview', sessionId] }),
                    queryClient.invalidateQueries({ queryKey: ['checkout'] }),
                ]);
            }
        },
    });
    // Track operation states
    const [isSelecting, setIsSelecting] = useState(false);
    const [isCanceling, setIsCanceling] = useState(false);
    // Select all VIP offers
    const selectVipOffers = useCallback(async () => {
        if (!sessionId)
            return;
        setIsSelecting(true);
        try {
            // Toggle all offers that aren't already selected
            const togglePromises = vipOffers
                .filter((offer) => !isOfferSelected(offer))
                .map((offer) => toggleMutation.mutateAsync({ offerId: offer.id, selected: true }));
            await Promise.all(togglePromises);
            // Refresh preview to get updated data
            await refetchPreview();
            // Call success callback
            if (onSelectSuccess) {
                onSelectSuccess();
            }
        }
        catch (error) {
            console.error('Failed to select VIP offers:', error);
            throw error;
        }
        finally {
            setIsSelecting(false);
        }
    }, [sessionId, vipOffers, isOfferSelected, toggleMutation, refetchPreview, onSelectSuccess]);
    // Cancel all VIP offers
    const cancelVipOffers = useCallback(async () => {
        if (!sessionId)
            return;
        setIsCanceling(true);
        try {
            // Toggle all offers to deselected
            const togglePromises = vipOffers.map((offer) => toggleMutation.mutateAsync({ offerId: offer.id, selected: false }));
            await Promise.all(togglePromises);
            // Refresh preview to get updated data
            await refetchPreview();
            // Call success callback
            if (onCancelSuccess) {
                onCancelSuccess();
            }
        }
        catch (error) {
            console.error('Failed to cancel VIP offers:', error);
            throw error;
        }
        finally {
            setIsCanceling(false);
        }
    }, [sessionId, vipOffers, toggleMutation, refetchPreview, onCancelSuccess]);
    // Refresh VIP preview
    const refreshVipPreview = useCallback(async () => {
        await refetchPreview();
    }, [refetchPreview]);
    // Combine errors
    const error = (fetchError || toggleMutation.error);
    return {
        vipOffers,
        vipPreview,
        isLoadingVipPreview,
        hasVip,
        isOfferSelected,
        selectVipOffers,
        cancelVipOffers,
        refreshVipPreview,
        isSelecting,
        isCanceling,
        error,
    };
}
